import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Eye, EyeOff, FileText, AlertTriangle, Check, X, ArrowUp, ArrowDown, Settings, ToolCase, Sliders, Info, ChevronRight, Trash2, Calculator, Flag, FlagTriangleRight } from 'lucide-react';
import DocumentMappingModal from './DocumentMappingModal';
import DataTable from './DataTable';
import SearchPresets from './SearchPresets';
import LoadingSpinner from './LoadingSpinner';
import useFieldSaveLogic from '../hooks/useFieldSaveLogic';
import { apiFetch } from '../utils/apiUtils';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem;
  min-height: calc(100vh - 180px);
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
`;

const InfoTooltip = styled.div`
  position: relative;
  display: inline-block;
  
  .info-icon {
    color: #9ca3af;
    cursor: help;
    transition: color 0.2s;
    
    &:hover {
      color: #6b7280;
    }
  }
  
  .tooltip {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 0.5rem;
    background: #374151;
    color: white;
    padding: 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: nowrap;
    max-width: 400px;
    white-space: normal;
    width: 350px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    transition: opacity 0.2s, visibility 0.2s;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-bottom-color: #374151;
    }
  }
  
  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;



const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${props => props.status === 'active' && `
    background: #dcfce7;
    color: #166534;
  `}
  
  ${props => props.status === 'ignored' && `
    background: #fee2e2;
    color: #991b1b;
  `}
  
  ${props => props.status === 'mapped' && `
    background: #dbeafe;
    color: #1e40af;
  `}
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #4b5563;
  border-radius: 6px;
  background: #374151;
  color: #d1d5db;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: #4b5563;
    border-color: #6b7280;
    color: #f9fafb;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const DeleteButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-left: 0.5rem;
  
  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const DeleteModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DeleteModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const DeleteModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DeleteModalText = styled.p`
  color: #6b7280;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const DeleteModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const DeleteModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'danger' ? `
    background: #dc2626;
    color: white;
    border: 1px solid #dc2626;
    
    &:hover {
      background: #b91c1c;
      border-color: #b91c1c;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const DocumentTypeIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  margin-right: 0.0625rem;
  background: ${props => props.color || '#6b7280'};
`;

const PriorityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.0625rem;
`;

const PriorityArrow = styled.div`
  color: #9ca3af;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
`;

const IncludeToggle = styled.div`
  position: relative;
  width: 40px;
  height: 20px;
  background: ${props => props.enabled ? '#475569' : '#d1d5db'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:before {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: white;
    top: 4px;
    left: ${props => props.enabled ? '24px' : '4px'};
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ToggleLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${props => props.enabled ? '#475569' : '#9ca3af'};
  margin-left: 0.5rem;
`;


const FieldsManagement = ({ view = 'all' }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [systemFieldNames, setSystemFieldNames] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [isResetToDefault, setIsResetToDefault] = useState(false);
  const [isModalInitialized, setIsModalInitialized] = useState(false);

  // Use our bulletproof field save logic
  const { saveFieldChangesWithValidation, saveModalSettings, saveModalComprehensive, toggleFieldStatus } = useFieldSaveLogic();
  const [deleting, setDeleting] = useState(false);

  // Load system field names for match checking
  const loadSystemFieldNames = async () => {
    try {
      const response = await apiFetch('/system-files/system-field-names');
      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setSystemFieldNames(data.field_names || []);
        return data.field_names || [];
      }
    } catch (error) {
      console.error('Error loading system field names:', error);
    }
    return [];
  };

  // Load document types for icon mapping
  const loadDocumentTypes = async () => {
    try {
      const response = await apiFetch('/system-files/document-types');
      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setDocumentTypes(data.document_types || []);
        return data.document_types || [];
      }
    } catch (error) {
      console.error('Error loading document types:', error);
    }
    return [];
  };

  // Load real field data from API
  const fetchFields = async (isRefresh = false, systemFieldNamesToUse = null) => {
    try {
      // Only show loading spinner on initial load, not on refresh
      if (!isRefresh) {
        setLoading(true);
      }
      
      const response = await apiFetch('/system-files/fields/stats');
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (data.success) {
        // Transform API data to our format
        const transformedFields = data.fields.map((field, index) => {
          const namesToUse = systemFieldNamesToUse || systemFieldNames;
          const systemNameMatch = namesToUse.includes(field.system_name || field.name);
          
          return {
            // Use UUID when available to guarantee uniqueness; fall back to normalized name
            id: field.field_uuid || field.name.toLowerCase().replace(/\s+/g, '_'),
            name: field.name.toLowerCase().replace(/\s+/g, '_'),
            displayName: field.name,
            field_uuid: field.field_uuid, // Include field UUID for API calls
            system_name: field.system_name, // Include system name
            prompt_name: field.prompt_name, // Include prompt name for tooltip
            status: field.status || 'active', // Use real status from API
            documentMapping: field.document_mapping || 'any', // Use real mapping from API
            priority: field.priority || 5, // Use real priority from API
            document_priority: field.document_priority || [], // Include document priority
            enabled_documents: field.enabled_documents || {}, // Include enabled documents
            matching_hints: field.matching_hints || '', // Include matching hints
            is_calculated_field: field.is_calculated_field || false, // Include calculated field flag
            priority_flag: field.priority_flag || 'Critical', // Include priority flag
            system_name_match: systemNameMatch, // Check if system name matches
            foundCount: field.found_count,
            totalRecords: data.total_loans,
            completionRate: Math.round(field.completion_rate),
            sourceFiles: field.source_files || []
          };
        });
        
        setFields(transformedFields);
      } else {
        console.error('❌ API returned error:', data);
        // Only clear fields on initial load, not on refresh
        if (!isRefresh) {
          setFields([]);
        }
      }
    } catch (error) {
      console.error('❌ Error loading fields:', error);
      // Only clear fields on initial load, not on refresh
      if (!isRefresh) {
        setFields([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const systemFieldNames = await loadSystemFieldNames();
      await loadDocumentTypes();
      await fetchFields(false, systemFieldNames);
    };
    initializeData();
  }, []);

  // Expose refresh function to be called directly by parent components
  const refreshFieldsData = useCallback(async () => {
    console.log('🔄 Refreshing fields data');
    const systemFieldNames = await loadSystemFieldNames();
    await fetchFields(true, systemFieldNames);
  }, []);

  // Filter fields based on search and active filter
  const filteredFields = fields.filter(field => {
    const matchesSearch = field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         field.displayName.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesFilter = false;
    switch (activeFilter) {
      case 'all':
        matchesFilter = true;
        break;
      case 'active':
        matchesFilter = field.status === 'active';
        break;
      case 'ignored':
        matchesFilter = field.status === 'ignored';
        break;
      case 'calculated':
        matchesFilter = field.is_calculated_field === true;
        break;
      case 'critical':
        matchesFilter = field.priority_flag === 'Critical';
        break;
      case 'conditional':
        matchesFilter = field.priority_flag === 'Conditional';
        break;
      case 'high_exception':
        matchesFilter = field.priority_flag === 'High Exception';
        break;
      case 'custom':
        matchesFilter = (field.document_priority && field.document_priority.length > 0) && field.status === 'active';
        break;
      default:
        matchesFilter = field.status === activeFilter;
    }
    
    return matchesSearch && matchesFilter;
  });

  const handleStatusChange = async (fieldUuid, newStatus) => {
    const field = fields.find(f => f.field_uuid === fieldUuid);
    if (!field) {
      console.error('Field not found with UUID:', fieldUuid);
      return;
    }

    try {
      // Use simple status toggle - no need to send all the other field data
      const result = await toggleFieldStatus(field, newStatus);
      
      if (result.success) {
        // Optimistic update - update the field in our local state by UUID
        setFields(fields.map(f => 
          f.field_uuid === fieldUuid 
            ? { ...f, status: newStatus }
            : f
        ));
        
        console.log(`✅ Field ${field.displayName} (UUID: ${fieldUuid}) status updated to ${newStatus}`);
        sessionStorage.setItem('needsDataRefresh', 'true');
      } else {
        console.error('❌ Failed to update field status:', result.error);
        // Could show user notification here
      }
    } catch (error) {
      console.error('❌ Error updating field status:', error);
    }
  };

  const handleDocumentMappingChange = async (fieldId, newMapping) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) {
      console.error('Field not found:', fieldId);
      return;
    }

    try {
      // Use our bulletproof save logic
      const changes = {
        fieldName: field.displayName,
        status: field.status,
        priority: field.priority,
        document_mapping: newMapping,
        matching_hints: field.matching_hints || ''
      };

      const result = await saveFieldChangesWithValidation(changes, field, {});
      
      if (result.success) {
        // Optimistic update
        setFields(fields.map(f => 
          f.id === fieldId 
            ? { ...f, documentMapping: newMapping }
            : f
        ));
        
        console.log(`✅ Field ${fieldId} document mapping updated to ${newMapping}`);
      } else {
        console.error('❌ Failed to update document mapping:', result.error);
      }
    } catch (error) {
      console.error('❌ Error updating document mapping:', error);
    }
  };

  const handlePriorityChange = async (fieldId, newPriority) => {
    const priority = parseInt(newPriority) || 1;
    const field = fields.find(f => f.id === fieldId);
    
    if (!field) {
      console.error('Field not found:', fieldId);
      return;
    }

    try {
      // Use our bulletproof save logic
      const changes = {
        fieldName: field.displayName,
        status: field.status,
        priority: priority,
        document_mapping: field.document_mapping,
        matching_hints: field.matching_hints || ''
      };

      const result = await saveFieldChangesWithValidation(changes, field, {});
      
      if (result.success) {
        // Optimistic update
        setFields(fields.map(f => 
          f.id === fieldId 
            ? { ...f, priority }
            : f
        ));
        
        console.log(`✅ Field ${fieldId} priority updated to ${priority}`);
      } else {
        console.error('❌ Failed to update field priority:', result.error);
      }
    } catch (error) {
      console.error('❌ Error updating field priority:', error);
    }
  };

  const handleOpenMappingModal = (field) => {
    setSelectedField(field);
    setIsModalOpen(true);
    // Initialize modal data right here - no useEffect needed!
    // The modal will initialize itself when it renders
  };

  const handleCloseMappingModal = () => {
    setIsModalOpen(false);
    setSelectedField(null);
  };

  const handleSaveMappingSettings = async (settings, documents = [], changeDetection = {}, isResetToDefault = false, defaultPriorityOrder = []) => {
    try {
      if (!selectedField) {
        throw new Error('No field selected');
      }
      
      // Use our comprehensive hook to handle all modal saving
      const result = await saveModalComprehensive(settings, documents, selectedField, changeDetection, isResetToDefault, defaultPriorityOrder);
      
      if (result.success) {
        // Refresh the fields data from API to get latest state
        await refreshFieldsData();
        
        // Close modal after successful save and refresh
        handleCloseMappingModal();
      } else {
        console.error('❌ Failed to save modal settings:', result.error);
        // Could show user notification here
      }
      
    } catch (error) {
      console.error('❌ Error saving mapping settings:', error);
    }
  };

  const handleDeleteField = (field) => {
    setFieldToDelete(field);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fieldToDelete) return;
    
    setDeleting(true);
    try {
      const response = await apiFetch(`/system-files/fields/${fieldToDelete.name}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete field');
      }

      // Remove field from state
      setFields(fields.filter(field => field.name !== fieldToDelete.name));
      
      setDeleteModalOpen(false);
      setFieldToDelete(null);
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('Failed to delete field. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setFieldToDelete(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Check size={12} />;
      case 'ignored':
        return <EyeOff size={12} />;
      case 'mapped':
        return <FileText size={12} />;
      default:
        return null;
    }
  };

  const getDocumentTypeInfo = (type) => {
    // Try to find the document type in the available document types
    const documentType = documentTypes.find(dt => dt.name === type);
    if (documentType) {
      return {
        icon: documentType.display_name.charAt(0).toUpperCase(),
        color: documentType.color
      };
    }
    
    // Fallback to hardcoded mapping for backward compatibility
    switch (type) {
      case 'proposal':
        return { icon: 'P', color: '#059669' };
      case 'note':
        return { icon: 'N', color: '#2563eb' };
      case 'service_agreement':
        return { icon: 'S', color: '#d97706' };
      case 'additional':
      case 'other': // Backward compatibility
        return { icon: 'A', color: '#7c3aed' };
      default:
        return { icon: '?', color: '#6b7280' };
    }
  };

  const renderPriorityIcons = (field) => {
    if (field.status === 'ignored') {
      return (
        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
          Not applicable
        </span>
      );
    }
    
    // Use configured priority order if available, otherwise show "Default"
    if (!field.document_priority || field.document_priority.length === 0) {
      return (
        <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
          Default
        </span>
      );
    }
    
    // Filter to only show enabled documents if configured
    let displayOrder = field.document_priority;
    if (field.enabled_documents && Object.keys(field.enabled_documents).length > 0) {
      displayOrder = field.document_priority.filter(type => field.enabled_documents[type] !== false);
    }
    
    if (displayOrder.length === 0) {
      return (
        <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
          Default
        </span>
      );
    }
    
    return (
      <PriorityContainer>
        {displayOrder.map((type, idx) => {
          // Convert document name to ID if needed (handle both "Note" and "note" formats)
          const documentId = type.toLowerCase().replace(/\s+/g, '_');
          const documentInfo = getDocumentTypeInfo(documentId);
          return (
            <React.Fragment key={type}>
              <DocumentTypeIcon 
                data-cy="document-priority-preview-tile"
                color={documentInfo.color}
                title={type.replace('_', ' ')}
              >
                {documentInfo.icon}
              </DocumentTypeIcon>
              {idx < displayOrder.length - 1 && (
                <PriorityArrow>
                  <ChevronRight size={12} />
                </PriorityArrow>
              )}
            </React.Fragment>
          );
        })}
      </PriorityContainer>
    );
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <LoadingSpinner size="large" />
          <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading field configuration...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <ToolCase size={24} />
          Field Management
          <InfoTooltip>
            <Info size={18} className="info-icon" />
            <div className="tooltip">
              Control which fields are included in document matching and how they're prioritized. 
              These settings affect how the system extracts and matches data from uploaded documents.
            </div>
          </InfoTooltip>
        </Title>
        <Subtitle>
          View field completion rates across documents and configure extraction priorities for optimal matching
        </Subtitle>
      </Header>

      <Controls>
        <SearchInput
          type="text"
          placeholder="Search fields by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-cy="filter-field-name"
        />
        
        <SearchPresets
          presets={[
            { key: 'all', label: 'All Fields', count: fields.length },
            { key: 'active', label: 'Included', count: fields.filter(f => f.status === 'active').length },
            { key: 'ignored', label: 'Excluded', count: fields.filter(f => f.status === 'ignored').length },
            { key: 'calculated', label: 'Calculated', count: fields.filter(f => f.is_calculated_field).length },
            { key: 'critical', label: 'Critical', count: fields.filter(f => f.priority_flag === 'Critical').length },
            { key: 'conditional', label: 'Conditional', count: fields.filter(f => f.priority_flag === 'Conditional').length },
            { key: 'high_exception', label: 'High Exception', count: fields.filter(f => f.priority_flag === 'High Exception').length },
            { key: 'custom', label: 'Custom Priority', count: fields.filter(f => (f.document_priority && f.document_priority.length > 0) && f.status === 'active').length }
          ]}
          activePreset={activeFilter}
          onPresetChange={setActiveFilter}
        />
      </Controls>

      <DataTable 
        data-cy="fields-list"
        rowDataCy="field-row"
        getRowAttributes={(field) => ({
          'data-field-uuid': field.field_uuid,
          'data-cy': 'field-row'
        })}
        columns={[
          {
            header: '',
            field: 'system_match',
            width: '5%',
            render: (field) => (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                opacity: field.status === 'ignored' ? 0.5 : 1
              }}>
                {field.is_calculated_field ? (
                  <div data-cy="calculated-field" style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                  }}>
                    <Calculator size={12} />
                  </div>
                ) : field.system_name_match ? (
                  <Check 
                    data-cy="system-match-true"
                    size={12} 
                    color="#059669"
                    style={{ fontWeight: '600' }}
                  />
                ) : (
                  <span 
                    data-cy="system-match-false"
                    style={{ 
                      color: '#9ca3af',
                      fontSize: '0.75rem'
                    }}
                  >
                    ?
                  </span>
                )}
              </div>
            )
          },
          {
            header: '',
            field: 'priority_flag',
            width: '5%',
            render: (field) => (
              <div 
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: field.status === 'ignored' ? 0.5 : 1
                }}
                title={`Priority: ${field.priority_flag || 'Critical'}`}
              >
                <FlagTriangleRight 
                  size={14} 
                  color={
                    field.priority_flag === 'Critical' ? '#dc2626' : 
                    field.priority_flag === 'Conditional' ? '#ef4444' : '#2563eb'
                  } 
                />
              </div>
            )
          },
          {
            header: 'Field Name',
            field: 'displayName',
            width: '20%',
            render: (field) => (
              <div style={{ 
                opacity: field.status === 'ignored' ? 0.5 : 1,
                transition: 'opacity 0.2s',
                position: 'relative',
                cursor: 'help'
              }}>
                <div 
                  style={{ fontWeight: '600', fontSize: '0.875rem' }}
                  title={`System Name: ${field.displayName}${field.prompt_name && field.prompt_name !== field.displayName ? `\nPrompt Name: ${field.prompt_name}` : ''}`}
                >
                  {field.displayName}
                </div>
              </div>
            )
          },
          {
            header: 'Include in Matching',
            field: 'status',
            width: '15%',
            render: (field) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div 
                  data-cy="field-status-toggle" 
                  data-status={field.status}
                >
                  <IncludeToggle 
                    enabled={field.status !== 'ignored'}
                    onClick={() => handleStatusChange(field.field_uuid, field.status === 'ignored' ? 'active' : 'ignored')}
                  />
                </div>
                <div data-cy="field-status-label">
                  <ToggleLabel enabled={field.status !== 'ignored'}>
                    {field.status === 'ignored' ? 'Excluded' : 'Included'}
                  </ToggleLabel>
                </div>
              </div>
            )
          },
          {
            header: 'Match Priority',
            field: 'priority',
            width: '30%',
            render: (field) => (
              field.status === 'ignored' ? (
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: '#9ca3af', 
                  fontStyle: 'italic' 
                }}>
                  Not applicable
                </span>
              ) : (
                <div style={{ 
                  opacity: 1,
                  transition: 'opacity 0.2s'
                }}>
                  {renderPriorityIcons(field)}
                </div>
              )
            )
          },
          {
            header: 'Actions',
            field: 'actions',
            width: '10%',
            render: (field) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ActionButton
                  data-cy="field-edit-button"
                  onClick={() => handleOpenMappingModal(field)}
                  title="Configure document matching priority"
                  disabled={field.status === 'ignored'}
                  style={{
                    opacity: field.status === 'ignored' ? 0.5 : 1,
                    cursor: field.status === 'ignored' ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Sliders size={16} />
                </ActionButton>
                <DeleteButton
                  onClick={() => handleDeleteField(field)}
                  title="Delete field"
                >
                  <Trash2 size={16} />
                </DeleteButton>
              </div>
            )
          }
        ]}
        data={filteredFields}
        keyField="id"
      />

      {filteredFields.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#6b7280' 
        }}>
          No fields found matching your criteria.
        </div>
      )}

      <DocumentMappingModal
        isOpen={isModalOpen}
        field={selectedField}
        onClose={handleCloseMappingModal}
        onSave={handleSaveMappingSettings}
        isResetToDefault={isResetToDefault}
        setIsResetToDefault={setIsResetToDefault}
        isModalInitialized={isModalInitialized}
        setIsModalInitialized={setIsModalInitialized}
      />

      {deleteModalOpen && (
        <DeleteModalOverlay>
          <DeleteModalContainer>
            <DeleteModalTitle>
              <Trash2 size={20} />
              Delete Field
            </DeleteModalTitle>
            <DeleteModalText>
              Are you sure you want to delete the field <strong>"{fieldToDelete?.name}"</strong>? 
              This will permanently remove all data associated with this field and cannot be undone.
            </DeleteModalText>
            <DeleteModalActions>
              <DeleteModalButton onClick={handleCancelDelete}>
                Cancel
              </DeleteModalButton>
              <DeleteModalButton 
                variant="danger" 
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Field'}
              </DeleteModalButton>
            </DeleteModalActions>
          </DeleteModalContainer>
        </DeleteModalOverlay>
      )}
    </Container>
  );
};

export default FieldsManagement;
